// Puff 3D del drop: modello procedurale fatto a codice, provvisorio finché non c'è un GLB.
// Reference: assets-reference/03_PRODUCT_REAL (forma, spicchi, trapuntatura, picciolo) e
// 01_DESKTOP_PRIMARY (controluce viola, fumo basso, pavimento lucido con riflesso).
// Si appoggia a un'immagine statica (poster) già visibile: se WebGL manca o la connessione
// chiede di risparmiare dati, il 3D non parte e resta il poster.
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const canvas = document.querySelector('[data-puff]');
const stage = canvas?.closest('[data-puff-stage]');

const posterMode = canvas?.hasAttribute('data-poster-mode'); // solo per generare il poster
const saveData = navigator.connection?.saveData === true;

function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

if (canvas && stage && hasWebGL() && (posterMode || !saveData)) init();

function init() {
  const reduceMotion = posterMode || matchMedia('(prefers-reduced-motion: reduce)').matches;
  const small = !posterMode && Math.min(innerWidth, innerHeight) < 600; // telefono: meno poligoni e texture
  const fabricColor = new THREE.Color(canvas.dataset.fabric || '#9552EC');

  const GORES = 4;          // spicchi del puff (cucitura centrale e metà gonfie, come il prodotto)
  const SEAM0 = 0.02;       // longitudine (0..1) della prima cucitura
  const FLOOR = -0.79;      // quota del pavimento (il puff ci si appoggia appena sopra)
  const PURPLE = new THREE.Color('#9b5cff');
  const SMOKE = new THREE.Color('#c9a6ff'); // fumo a terra, più chiaro

  // ---------- Utilità ----------
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const smoothstep = (e0, e1, x) => { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); };
  const damp = (a, b, lambda, dt) => THREE.MathUtils.lerp(a, b, 1 - Math.exp(-lambda * dt));
  function rng(seed) { // mulberry32: stesso puff a ogni caricamento
    return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
  }
  const mkCanvas = (w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; };
  // distanza (0 = sulla cucitura, 0.5 = centro dello spicchio) in unità di spicchio
  const goreDist = (u) => { const g = (((u - SEAM0) * GORES) % 1 + 1) % 1; return Math.min(g, 1 - g); };

  // ---------- Renderer, scena, camera ----------
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: posterMode,
  });
  renderer.setPixelRatio(posterMode ? 2 : Math.min(devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping; // fedele ai colori del prodotto
  renderer.toneMappingExposure = 1.12;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.22;

  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 60);
  const lookTarget = new THREE.Vector3(0, 0.06, 0);

  // Luci da studio: chiave morbida dall'alto, due controluce viola che accendono i bordi del velluto.
  scene.add(new THREE.HemisphereLight(0xcdb4ff, 0x08070a, 0.35));
  const key = new THREE.DirectionalLight(0xfff0f4, 3.0);
  key.position.set(-2.2, 4.2, 3);
  scene.add(key);
  for (const x of [-1, 1]) {
    const rim = new THREE.DirectionalLight(0xa066ff, 6.5);
    rim.position.set(3.2 * x, 1.2, -3.4);
    scene.add(rim);
  }
  const top = new THREE.DirectionalLight(0xffe2b0, 1.2); // accende il picciolo
  top.position.set(0.5, 5, 0.5);
  scene.add(top);
  const fill = new THREE.DirectionalLight(0x8f63ff, 0.5);
  fill.position.set(2.5, -0.5, 3);
  scene.add(fill);

  // ---------- Trapuntatura: spirali e code, disegnate per spicchio ----------
  // Ogni spicchio porta due spirali sfalsate (come il prodotto), con la coda che si apre verso la cucitura.
  function spiralLayout(rand) {
    const list = [];
    for (let k = 0; k < GORES; k++) {
      const u = SEAM0 + (k + 0.5) / GORES;
      const shift = k % 2 ? 0.14 : -0.08;
      list.push({ u: u - 0.05 + (rand() - 0.5) * 0.02, lat: 0.45 + shift, dir: k % 2 ? 1 : -1 });
      list.push({ u: u + 0.06 + (rand() - 0.5) * 0.02, lat: -0.3 + shift, dir: k % 2 ? -1 : 1 });
    }
    return list;
  }

  const spiralPoints = (R, turns, dir, rot) => {
    const pts = [];
    const T = turns * Math.PI * 2;
    for (let t = 0.5; t <= T; t += 0.05) {
      const r = (R * t) / T, a = dir * t + rot;
      pts.push([r * Math.cos(a), r * Math.sin(a)]);
    }
    for (let t = 0; t <= 1; t += 0.025) { // coda che si allarga
      const a = dir * (T + t * Math.PI * 1.1) + rot, r = R * (1 + t * 0.85);
      pts.push([r * Math.cos(a), r * Math.sin(a)]);
    }
    return pts;
  };

  // Disegna tutte le spirali su un contesto 2D (equirettangolare), con le tre copie orizzontali anti-giuntura.
  function drawSpirals(ctx, W, H, layout, strokes) {
    const S = W / 2048;
    const rand = rng(11);
    for (const s of layout) {
      const x = s.u * W, y = ((Math.PI / 2 - s.lat) / Math.PI) * H;
      const stretch = Math.min(1 / Math.cos(s.lat), 3.2); // compensa la deformazione verso i poli
      const R = (s.r ?? 1) * (175 + rand() * 25) * S;
      const pts = spiralPoints(R, 2.3 + rand() * 0.4, s.dir, rand() * Math.PI * 2);
      for (const dx of [-W, 0, W]) {
        ctx.beginPath();
        pts.forEach(([px, py], i) => { const X = x + dx + px * stretch, Y = y + py; i ? ctx.lineTo(X, Y) : ctx.moveTo(X, Y); });
        for (const [style, width] of strokes) { ctx.strokeStyle = style; ctx.lineWidth = width * S; ctx.stroke(); }
      }
    }
  }

  // ---------- Texture del velluto ----------
  function makeFabric(layout) {
    const W = small ? 1024 : 2048, H = W / 2, S = W / 2048;
    const rand = rng(7);
    const col = mkCanvas(W, H), bump = mkCanvas(W, H);
    const c = col.getContext('2d'), b = bump.getContext('2d');
    c.lineCap = c.lineJoin = b.lineCap = b.lineJoin = 'round';

    c.fillStyle = '#' + fabricColor.getHexString(); c.fillRect(0, 0, W, H);
    b.fillStyle = 'rgb(140,140,140)'; b.fillRect(0, 0, W, H);

    // Velluto "crushed": macchie allungate chiare/scure orientate a caso (il pelo che cambia verso).
    for (let i = 0; i < 1400; i++) {
      const x = rand() * W, y = rand() * H, rx = (20 + rand() * 110) * S, ry = rx * (0.22 + rand() * 0.4);
      const light = rand() < 0.5, a = rand() * Math.PI;
      for (const ctx of [c, b]) {
        ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.scale(1, ry / rx);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
        const v = ctx === c ? (light ? 'rgba(255,215,255,0.12)' : 'rgba(60,0,90,0.1)') : (light ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)');
        g.addColorStop(0, v); g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, rx, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      }
    }

    // Cuciture fra gli spicchi: solco nel rilievo + ombra nel colore.
    for (let k = 0; k < GORES; k++) {
      const x = (SEAM0 + k / GORES) * W;
      for (const dx of [-W, 0, W]) {
        for (const [w, a] of [[26, 0.1], [12, 0.25], [4, 0.6]]) {
          b.strokeStyle = `rgba(20,20,20,${a})`; b.lineWidth = w * S;
          b.beginPath(); b.moveTo(x + dx, 0); b.lineTo(x + dx, H); b.stroke();
        }
        c.strokeStyle = 'rgba(40,8,90,0.22)'; c.lineWidth = 12 * S;
        c.beginPath(); c.moveTo(x + dx, 0); c.lineTo(x + dx, H); c.stroke();
      }
    }

    // Trapuntatura: solco profondo a bordi morbidi, ombra scura dentro, filo appena visibile.
    drawSpirals(b, W, H, layout, [['rgba(0,0,0,0.06)', 44], ['rgba(0,0,0,0.1)', 26], ['rgba(0,0,0,0.22)', 13], ['rgba(0,0,0,0.4)', 6]]);
    drawSpirals(c, W, H, layout, [['rgba(40,5,90,0.12)', 30], ['rgba(40,5,90,0.18)', 12], ['rgba(215,185,255,0.12)', 2]]);

    // Pelo fine: rumore per pixel (granulosità del velluto da vicino).
    for (const [ctx, amp] of [[c, 8], [b, 7]]) {
      const img = ctx.getImageData(0, 0, W, H), d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        const n = (rand() - 0.5) * amp;
        d[i] += n; d[i + 1] += n; d[i + 2] += n;
      }
      ctx.putImageData(img, 0, 0);
    }

    const map = new THREE.CanvasTexture(col);
    map.colorSpace = THREE.SRGBColorSpace;
    const bumpMap = new THREE.CanvasTexture(bump);
    for (const t of [map, bumpMap]) {
      t.wrapS = THREE.RepeatWrapping;
      t.anisotropy = renderer.capabilities.getMaxAnisotropy();
    }
    return { map, bumpMap };
  }

  // Rilievo grosso (gonfiore fra i solchi) letto dalla geometria, non solo dalla luce.
  function makePillowField(layout) {
    const W = 512, H = 256;
    const cv = mkCanvas(W, H), ctx = cv.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H);
    ctx.lineCap = ctx.lineJoin = 'round';
    drawSpirals(ctx, W, H, layout, [['rgba(0,0,0,0.25)', 120], ['rgba(0,0,0,0.35)', 60], ['rgba(0,0,0,0.5)', 26]]);
    const d = ctx.getImageData(0, 0, W, H).data;
    return (u, v) => { // 0 = solco, 1 = gonfio
      const x = Math.floor((((u % 1) + 1) % 1) * (W - 1)), y = Math.floor(clamp(v, 0, 1) * (H - 1));
      return d[(y * W + x) * 4] / 255;
    };
  }

  // ---------- Corpo del puff ----------
  function makeBody(layout) {
    const geo = new THREE.SphereGeometry(1, small ? 220 : 340, small ? 150 : 230);
    const pos = geo.attributes.position, uv = geo.attributes.uv;
    const pillow = makePillowField(layout);
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const u = uv.getX(i), vv = 1 - uv.getY(i);
      const lat = Math.asin(clamp(v.y, -1, 1));
      const band = Math.pow(Math.cos(lat), 0.6);           // gli effetti spariscono ai poli
      const g = goreDist(u);
      let r = 1;
      r *= 1 + 0.04 * Math.sin(Math.PI * g * 2) * band;      // spicchio gonfio al centro
      r *= 1 - 0.045 * Math.exp(-((g / 0.05) ** 2)) * band;  // cucitura incassata
      r *= 1 + 0.03 * (pillow(u, vv) - 0.75) * band;         // trapuntatura: gonfio fra i solchi
      let x = v.x * r, y = v.y * r, z = v.z * r;
      // zucca larga e schiacciata (≈ 85 × 70 cm), fianchi pieni, base appoggiata
      y *= 0.8;
      const widen = 1 + 0.07 * smoothstep(0.5, -0.5, y);
      x *= widen; z *= widen;
      const floor = -0.72;
      if (y < floor + 0.12) {
        const k = smoothstep(floor + 0.12, floor - 0.1, y);
        y = THREE.MathUtils.lerp(y, floor + (y - floor) * 0.2, k);
      }
      const r2 = x * x + z * z;                              // conca del picciolo
      y -= 0.1 * Math.exp(-r2 / 0.035) * smoothstep(0.3, 0.8, y);
      pos.setXYZ(i, x, y, z);
    }
    geo.computeVertexNormals();
    smoothSeamNormals(geo);

    const { map, bumpMap } = makeFabric(layout);
    const mat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      map,
      bumpMap,
      bumpScale: 3.2,
      roughness: 1,
      sheen: 1,
      sheenRoughness: 0.3,
      sheenColor: new THREE.Color('#f2d9ff'),
    });
    return new THREE.Mesh(geo, mat);
  }

  // La sfera ha vertici duplicati sulla giuntura: media delle normali per non vedere la riga.
  function smoothSeamNormals(geo) {
    const pos = geo.attributes.position, nor = geo.attributes.normal;
    const groups = new Map();
    for (let i = 0; i < pos.count; i++) {
      const k = `${pos.getX(i).toFixed(4)},${pos.getY(i).toFixed(4)},${pos.getZ(i).toFixed(4)}`;
      (groups.get(k) || groups.set(k, []).get(k)).push(i);
    }
    const n = new THREE.Vector3();
    for (const idx of groups.values()) {
      if (idx.length < 2) continue;
      n.set(0, 0, 0);
      for (const i of idx) n.x += nor.getX(i), n.y += nor.getY(i), n.z += nor.getZ(i);
      n.normalize();
      for (const i of idx) nor.setXYZ(i, n.x, n.y, n.z);
    }
  }

  // ---------- Picciolo: ramo orizzontale sopra il puff, ricciolo che scende su un lato ----------
  function taperedTube(curve, segs, radius) {
    const radial = 24;
    const geo = new THREE.TubeGeometry(curve, segs, 1, radial, false);
    const pos = geo.attributes.position;
    const p = new THREE.Vector3(), v = new THREE.Vector3();
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      curve.getPointAt(t, p);
      const r = radius(t);
      for (let j = 0; j <= radial; j++) {
        const k = i * (radial + 1) + j;
        v.fromBufferAttribute(pos, k).sub(p).multiplyScalar(r).add(p);
        pos.setXYZ(k, v.x, v.y, v.z);
      }
    }
    geo.computeVertexNormals();
    return geo;
  }

  function makeStem() {
    const plush = mkCanvas(256, 256), pc = plush.getContext('2d'), rand = rng(3);
    pc.fillStyle = '#888'; pc.fillRect(0, 0, 256, 256);
    for (let i = 0; i < 2500; i++) { pc.fillStyle = rand() < 0.5 ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'; pc.fillRect(rand() * 256, rand() * 256, 2, 2); }
    const bumpMap = new THREE.CanvasTexture(plush);
    bumpMap.wrapS = bumpMap.wrapT = THREE.RepeatWrapping;
    bumpMap.repeat.set(6, 2);

    const mat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color('#f5b631'),
      roughness: 0.85,
      bumpMap,
      bumpScale: 1.2,
      sheen: 1,
      sheenRoughness: 0.4,
      sheenColor: new THREE.Color('#fff0b8'),
    });
    const group = new THREE.Group();

    // Adagiato sulla sommità: ricciolo appoggiato sulla spalla sinistra, ramo che passa sopra il centro,
    // punta tonda che sale a destra (come nella foto del prodotto).
    const pts = [];
    const c = new THREE.Vector2(-0.43, 0.8), turns = 1.2;
    for (let t = turns; t >= 0; t -= 0.03) {
      const a = Math.PI * 0.5 + t * Math.PI * 2;
      const r = 0.13 * (0.3 + 0.7 * (1 - t / turns));
      pts.push(new THREE.Vector3(c.x + r * Math.cos(a), c.y + r * Math.sin(a), 0.08 + 0.06 * (t / turns)));
    }
    pts.push(
      new THREE.Vector3(-0.26, 0.9, 0.05), new THREE.Vector3(-0.06, 0.88, 0.02), new THREE.Vector3(0.16, 0.87, 0),
      new THREE.Vector3(0.36, 0.92, -0.02), new THREE.Vector3(0.5, 1.03, -0.03),
    );
    const curve = new THREE.CatmullRomCurve3(pts, false, 'centripetal');
    // sottile nel ricciolo, pieno verso la punta
    group.add(new THREE.Mesh(taperedTube(curve, 260, (t) => THREE.MathUtils.lerp(0.05, 0.115, smoothstep(0, 0.5, t))), mat));
    const tip = new THREE.Mesh(new THREE.SphereGeometry(0.115, 24, 16), mat);
    tip.position.copy(curve.getPointAt(1));
    group.add(tip);
    const curlEnd = new THREE.Mesh(new THREE.SphereGeometry(0.05, 16, 12), mat);
    curlEnd.position.copy(curve.getPointAt(0));
    group.add(curlEnd);

    // attacco corto nella conca, nascosto sotto il ramo
    const neck = new THREE.CatmullRomCurve3([new THREE.Vector3(0.0, 0.64, 0), new THREE.Vector3(-0.02, 0.76, 0.01), new THREE.Vector3(-0.04, 0.87, 0.02)]);
    group.add(new THREE.Mesh(taperedTube(neck, 24, () => 0.105), mat));

    group.rotation.y = -0.35;
    return group;
  }

  // ---------- Pavimento lucido: riflesso attenuato, pozza di luce viola, si dissolve sullo sfondo ----------
  function makeFloor() {
    const N = small ? 256 : 512, SIZE = 14;
    const cv = mkCanvas(N, N), ctx = cv.getContext('2d');
    const img = ctx.createImageData(N, N), d = img.data;
    const bg = [8, 7, 10], pr = [155, 92, 255];
    for (let j = 0; j < N; j++) {
      const z = -SIZE / 2 + (j / (N - 1)) * SIZE; // riga 0 = fondo della scena
      for (let i = 0; i < N; i++) {
        const x = -SIZE / 2 + (i / (N - 1)) * SIZE;
        const r = Math.hypot(x, z * 1.15);
        const glow = 0.36 * Math.exp(-((r / 1.35) ** 2)) + 0.07 * Math.exp(-((r / 3) ** 2));
        const pool = THREE.MathUtils.lerp(0.74, 1, smoothstep(0.9, 2.8, r)); // sotto il puff il riflesso traspare
        const fade = smoothstep(-6, -1.6, z) * (1 - smoothstep(3.6, 6.8, Math.abs(x)));
        const k = (j * N + i) * 4;
        for (let ch = 0; ch < 3; ch++) d[k + ch] = bg[ch] + (pr[ch] - bg[ch]) * glow;
        d[k + 3] = 255 * pool * fade;
      }
    }
    ctx.putImageData(img, 0, 0);
    const map = new THREE.CanvasTexture(cv);
    map.colorSpace = THREE.SRGBColorSpace;
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(SIZE, SIZE),
      new THREE.MeshBasicMaterial({ map, transparent: true, depthWrite: false }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = FLOOR;
    mesh.renderOrder = 1;
    return mesh;
  }

  // ---------- Ombra di contatto ----------
  function makeShadow() {
    const s = 256, cv = mkCanvas(s, s), ctx = cv.getContext('2d');
    const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    g.addColorStop(0, 'rgba(0,0,0,0.8)'); g.addColorStop(0.55, 'rgba(0,0,0,0.35)'); g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, s, s);
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(2.7, 2.2),
      new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(cv), transparent: true, depthWrite: false }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = FLOOR + 0.004;
    mesh.renderOrder = 2;
    return mesh;
  }

  // ---------- Fumo basso viola: piani morbidi che derivano piano ----------
  function makeSmoke() {
    const S = 256, cv = mkCanvas(S * 2, S), ctx = cv.getContext('2d'), rand = rng(21);
    for (let i = 0; i < 90; i++) { // nuvola: tante macchie morbide, più dense al centro
      const x = S * (0.25 + rand() * 1.5), y = S * (0.3 + rand() * 0.4), r = S * (0.08 + rand() * 0.22);
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(255,255,255,${0.05 + rand() * 0.07})`); g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g; ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
    }
    const tex = new THREE.CanvasTexture(cv);
    const layers = [
      // [x, y, z, larghezza, altezza, opacità, inclinazione]
      [0, -0.25, -2.4, 8, 3.2, 0.55, 0],
      [-0.6, 0.35, -1.9, 5.5, 2.4, 0.3, 0],
      [0.2, -0.66, 0.95, 6, 1.2, 0.6, -0.3],
      [-1.7, -0.56, 0.1, 3.6, 1.3, 0.55, -0.2],
      [1.8, -0.58, -0.2, 3.6, 1.3, 0.55, -0.2],
    ];
    return layers.map(([x, y, z, w, h, o, rx], i) => {
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({ map: tex, color: i < 2 ? PURPLE : SMOKE, transparent: true, opacity: o, depthWrite: false }),
      );
      m.position.set(x, y, z);
      m.rotation.x = rx;
      m.renderOrder = 3;
      m.userData = { x, o, phase: i * 1.7 };
      if (i % 2) m.scale.x = -1;
      return m;
    });
  }

  // ---------- Alone dietro il puff: la luce viola che lo stacca dal nero ----------
  function makeHalo() {
    const S = 256, cv = mkCanvas(S, S), ctx = cv.getContext('2d');
    const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(235,215,255,0.9)'); g.addColorStop(0.25, 'rgba(170,110,255,0.55)');
    g.addColorStop(0.6, 'rgba(120,60,230,0.18)'); g.addColorStop(1, 'rgba(120,60,230,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(4.2, 3.2),
      new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(cv), transparent: true, opacity: 0.55, depthWrite: false }),
    );
    m.position.set(0, 0.05, -1.4);
    return m;
  }

  // ---------- Montaggio ----------
  const layout = spiralLayout(rng(5));
  const puff = new THREE.Group();
  puff.add(makeBody(layout));
  puff.add(makeStem());
  const tilt = new THREE.Group(); // inclinazione separata dalla rotazione
  tilt.rotation.x = 0.08;
  tilt.add(puff);
  const floater = new THREE.Group(); // fluttuazione separata da tutto il resto
  floater.add(tilt);
  scene.add(floater);

  // Riflesso: copia specchiata sotto il pavimento (stesse geometrie e materiali), velata dal pavimento.
  const mirror = floater.clone();
  mirror.scale.y = -1;
  scene.add(mirror);
  const mirrorTilt = mirror.children[0], mirrorPuff = mirrorTilt.children[0];

  const floor = makeFloor();
  scene.add(floor);
  const shadow = makeShadow();
  scene.add(shadow);
  const smoke = makeSmoke();
  smoke.forEach((m) => scene.add(m));
  const halo = makeHalo();
  scene.add(halo);

  // ---------- Inquadratura: puff + pavimento con un po' d'aria ----------
  let running = false;
  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    // Si inquadra per altezza (come il poster quadrato), salvo palchi più stretti che alti.
    const fitH = 3.0, minW = 2.9;
    const dist = Math.max(fitH / 2 / tanV, minW / 2 / (tanV * camera.aspect));
    camera.position.set(0, dist * 0.17, dist);
    camera.lookAt(lookTarget);
    camera.updateProjectionMatrix();
    if (!running) renderer.render(scene, camera);
  }

  // ---------- Interazione: trascina per ruotare, con inerzia ----------
  const state = {
    spin: 0.35, vel: 0, tiltX: 0, // 0.35 = stessa posa del poster (picciolo di profilo)
    dragging: false, lastX: 0, lastY: 0, lastMove: 0,
    lastInteract: -Infinity, auto: 0,
    hoverX: 0, hoverY: 0,
  };

  canvas.addEventListener('pointerdown', (e) => {
    state.dragging = true;
    state.lastX = e.clientX; state.lastY = e.clientY; state.lastMove = performance.now();
    state.vel = 0;
    canvas.setPointerCapture(e.pointerId);
    canvas.classList.add('is-grabbing');
    stage.classList.add('is-touched');
  });
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'mouse' && !state.dragging) { // parallasse leggera col mouse
      state.hoverX = (e.clientX / innerWidth) * 2 - 1;
      state.hoverY = (e.clientY / innerHeight) * 2 - 1;
    }
    if (!state.dragging) return;
    const now = performance.now();
    const dx = e.clientX - state.lastX, dy = e.clientY - state.lastY;
    const dt = Math.max(now - state.lastMove, 1);
    const k = 5.5 / Math.max(canvas.clientWidth, 1); // mezzo palco ≈ mezzo giro
    state.spin += dx * k;
    state.vel = (dx * k) / (dt / 1000);
    state.tiltX = clamp(state.tiltX + dy * 0.003, -0.3, 0.3);
    state.lastX = e.clientX; state.lastY = e.clientY; state.lastMove = now;
    state.lastInteract = now;
  });
  const release = () => {
    if (!state.dragging) return;
    state.dragging = false;
    if (performance.now() - state.lastMove > 80) state.vel = 0; // fermo prima di rilasciare = niente lancio
    state.lastInteract = performance.now();
    canvas.classList.remove('is-grabbing');
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release); // il browser ha preso il gesto per scrollare
  canvas.addEventListener('pointerleave', () => { state.hoverX = state.hoverY = 0; });

  // ---------- Loop ----------
  let visible = true, last = performance.now(), t = 0;

  function pose(dt) {
    puff.rotation.y = state.spin;
    const hx = reduceMotion ? 0 : state.hoverY * 0.05, hz = reduceMotion ? 0 : -state.hoverX * 0.04;
    tilt.rotation.x = damp(tilt.rotation.x, 0.08 + state.tiltX + hx, 4, dt);
    tilt.rotation.z = damp(tilt.rotation.z, (reduceMotion ? 0 : Math.sin(t * 0.6) * 0.025) + hz, 4, dt);
    floater.position.y = reduceMotion ? 0 : 0.02 + Math.sin(t * 0.9) * 0.025;

    // riflesso: stessa posa, specchiata rispetto al pavimento
    mirror.position.y = 2 * FLOOR - floater.position.y;
    mirrorTilt.rotation.copy(tilt.rotation);
    mirrorPuff.rotation.copy(puff.rotation);

    // l'ombra si stringe e sbiadisce quando il puff sale
    const lift = floater.position.y;
    shadow.scale.setScalar(1 - lift * 0.4);
    shadow.material.opacity = 0.9 - lift * 0.8;

    // fumo che deriva piano
    for (const m of smoke) {
      const { x, o, phase } = m.userData;
      m.position.x = x + (reduceMotion ? 0 : Math.sin(t * 0.07 + phase) * 0.35);
      m.material.opacity = o * (reduceMotion ? 1 : 0.85 + 0.15 * Math.sin(t * 0.25 + phase));
    }
  }

  function frame(now) {
    running = visible && !document.hidden;
    if (!running) return;
    const dt = Math.min((now - last) / 1000, 1 / 20);
    last = now;
    t += dt;

    const idle = now - state.lastInteract > 2500;
    if (!state.dragging) {
      state.spin += state.vel * dt;
      state.vel *= Math.exp(-3.2 * dt); // attrito
      state.tiltX = damp(state.tiltX, 0, 2.5, dt);
    }
    state.auto = damp(state.auto, idle && !reduceMotion ? 0.2 : 0, 1.2, dt);
    state.spin += state.auto * dt;

    pose(dt);
    renderer.render(scene, camera);
    requestAnimationFrame(frame);
  }

  function start() {
    if (running || posterMode) return;
    last = performance.now();
    requestAnimationFrame(frame);
  }

  new ResizeObserver(resize).observe(canvas);
  resize();

  if (posterMode) {
    // Posa fissa per l'immagine statica: solo il puff (pavimento, fumo e riflesso arrivano col 3D).
    floor.visible = mirror.visible = halo.visible = false;
    smoke.forEach((m) => { m.visible = false; });
    state.spin = parseFloat(canvas.dataset.posterSpin ?? state.spin);
    pose(1);
    renderer.render(scene, camera);
    canvas.dispatchEvent(new CustomEvent('puff:poster-ready', { bubbles: true }));
    return;
  }

  new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; if (visible) start(); }).observe(canvas);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) start(); });

  // Primo frame pronto: dissolvenza dal poster al 3D.
  pose(1);
  renderer.render(scene, camera);
  requestAnimationFrame(() => {
    canvas.classList.add('is-ready');
    stage.classList.add('is-3d');
  });
  start();
}
